import React, { useState, useContext, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Context } from '../../Context'
import './styles.scss'
import 'react-tabs/style/react-tabs.css'
import { Button } from 'primereact/button'
import 'react-datepicker/dist/react-datepicker.css'
import PropTypes from 'prop-types'
import Calendar from 'react-calendar'
import Select from 'react-select'
import { SchedulerService } from '../../services/Scheduler'
import { Growl } from 'primereact/growl'
import { Message } from 'primereact/message'
import { DateTime } from 'luxon'

import 'react-date-time-picker-popup/dist/index.css'

export const SchedulForm = ({
  idlaboratory,
  isLoading,
  onSubmit,
  cancelLink,
  selectedDay,
  setSelectedDay,
  keyHours,
  setKeyHours,
  podAvaiblesForReserve = null,
}) => {
  const { isAuth: token, userLogged } = useContext(Context)
  const [error, setError] = useState(null)
  const [startHour, setStartHour] = useState('')
  const [endHour, setEndHour] = useState('')
  const [startHourRows, setStartHourRows] = useState([])
  const [endHourRows, setEndHourRows] = useState([])
  const [, setScheduleData] = useState([]) // Mantener por compatibilidad, aunque ya no se usa en getGroupedHours
  const [availabilityByHour, setAvailabilityByHour] = useState({}) // { "21:00": { available: 2, total: 2 } }
  const growlRef = useRef(null)
  const isMounted = useRef(true)
  const abortControllerRef = useRef(new AbortController())
  const isLoadingData = useRef(false)

  // Este useEffect solo se ejecuta una vez al montar el componente
  useEffect(() => {
    isMounted.current = true

    // Cargar datos iniciales si ya hay una fecha seleccionada
    if (selectedDay) {
      const selectedDateUTC = selectedDay.toUTC()
      getData(selectedDateUTC.toISO()).then(() => {
        // Después de cargar datos, verificar disponibilidad
        if (isMounted.current) {
          checkAvailabilityForAllHours()
        }
      })
    }

    return () => {
      isMounted.current = false
      abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Función para mostrar mensajes
  const showGrowl = (severity, summary, detail) => {
    if (growlRef.current && isMounted.current) {
      growlRef.current.show({ severity, summary, detail })
    }
  }

  // Función para manejar el cambio de fecha
  const convertDate = (e) => {
    // Evitar múltiples llamadas si ya hay una en proceso
    if (isLoadingData.current) return

    // Abortar cualquier solicitud anterior pendiente
    abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()

    // Obtener la fecha seleccionada en hora local y tomar inicio del día
    const luxonDate = DateTime.fromJSDate(e).startOf('day')

    // Limpiar selecciones actuales
    setStartHour('')
    setEndHour('')
    setEndHourRows([])
    setSelectedDay(luxonDate)

    // Marcar como cargando y obtener datos
    isLoadingData.current = true
    // Convertir a UTC para consultar al servidor
    getData(luxonDate.toUTC().toISO())
      .then(() => {
        // Después de cargar datos, verificar disponibilidad
        if (isMounted.current) {
          checkAvailabilityForAllHours()
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError' && isMounted.current) {
          setError(error.message)
        }
      })
      .finally(() => {
        if (isMounted.current) {
          isLoadingData.current = false
        }
      })
  }

  // Crear opciones de hora de inicio
  const createHourStartSelect = () => {
    if (!isMounted.current) return

    // Verificar que keyHours tenga datos
    if (!keyHours || Object.keys(keyHours).length === 0) {
      setStartHourRows([])
      return
    }

    // Crear un Set para almacenar horas únicas basadas en el label
    const uniqueHours = new Set()

    // Recorrer cada pod en keyHours
    Object.values(keyHours).forEach((hoursArray) => {
      if (Array.isArray(hoursArray)) {
        // Para cada array de horas del pod
        hoursArray.forEach((hour) => {
          // Usar el label como identificador único
          uniqueHours.add(JSON.stringify(hour))
        })
      }
    })

    // Convertir el Set back a array y ordenar por hora
    const rows = Array.from(uniqueHours)
      .map((hourStr) => JSON.parse(hourStr))
      .sort((a, b) => {
        const hourA = parseInt(a.label)
        const hourB = parseInt(b.label)
        return hourA - hourB
      })

    if (isMounted.current) {
      setStartHourRows(rows)
    }
  }

  // Crear opciones de hora de fin basado en la hora de inicio seleccionada
  const createHourEndSelect = (selectedStartHour) => {
    if (!isMounted.current || !selectedStartHour) return

    // Estandarizar el valor a un formato DateTime en hora local
    let startDateTime
    try {
      if (typeof selectedStartHour === 'string') {
        startDateTime = DateTime.fromISO(selectedStartHour)
      } else if (selectedStartHour instanceof DateTime) {
        startDateTime = selectedStartHour
      } else if (selectedStartHour.value) {
        // Si es un objeto con value (podría ser string ISO o DateTime)
        if (typeof selectedStartHour.value === 'string') {
          startDateTime = DateTime.fromISO(selectedStartHour.value)
        } else {
          startDateTime = selectedStartHour.value
        }
      } else {
        return // No se pudo obtener un valor válido
      }

      if (!startDateTime.isValid) return

      const startHour = startDateTime.hour
      const rowsEnd = []

      // Siempre mostrar las dos horas siguientes si no es la última hora del día
      if (startHour < 23) {
        // Primera hora después
        rowsEnd.push({
          label: `${startHour + 1}:00`,
          value: startDateTime.plus({ hours: 1 }).toISO(),
        })

        // Segunda hora después (si no excede las 23:00)
        if (startHour < 22) {
          rowsEnd.push({
            label: `${startHour + 2}:00`,
            value: startDateTime.plus({ hours: 2 }).toISO(),
          })
        }
      }

      if (isMounted.current) {
        setEndHourRows(rowsEnd)
      }
    } catch (error) {
      setError('Error al generar las horas de fin. Por favor, seleccione otra hora de inicio.')
    }
  }

  // Manejar cambio de hora de inicio
  const handleStarSelectChange = (e) => {
    if (!isMounted.current || !e) return

    // Resetear la hora fin cada vez que cambie la hora inicio
    setEndHour('')
    setStartHour(e.value)
    createHourEndSelect(e.value)
  }

  // Manejar cambio de hora de fin
  const handleEndSelectChange = (e) => {
    if (!isMounted.current || !e) return
    setEndHour(e.value)
  }

  // Obtener datos del día seleccionado
  const getData = async (dateISOString) => {
    if (!isMounted.current || !dateISOString)
      return Promise.reject(new Error('Componente desmontado'))

    try {
      const signal = abortControllerRef.current.signal

      // dateISOString puede venir con offset local o UTC, normalizar a UTC+0
      // Parsear la fecha respetando su zona y convertir a UTC
      let parsedDate = DateTime.fromISO(dateISOString)
      if (!parsedDate.isValid) {
        // Si falla, intentar parsear como UTC
        parsedDate = DateTime.fromISO(dateISOString, { zone: 'utc' })
      }

      // Convertir a UTC (mantener las horas originales, igual que el día siguiente)
      const selectedDateUTC = parsedDate.toUTC()
      const selectedDateLocal = selectedDateUTC.toLocal()

      // Obtener la fecha del día siguiente y convertir a UTC para consultar al servidor
      const nextDateLocal = selectedDateLocal.plus({ days: 1 })
      const nextDateUTC = nextDateLocal.toUTC()

      // Obtener datos del servicio para ambos días (enviar fechas en UTC+0 con formato Z)
      const currentDateISO = selectedDateUTC.toISO()
      const nextDateISO = nextDateUTC.toISO()

      const [currentDayData, nextDayData] = await Promise.all([
        // Obtener datos del día actual - usar selectedDateUTC.toISO() igual que el día siguiente
        SchedulerService.getByDay(currentDateISO, idlaboratory, token, { signal }),
        // Obtener datos del día siguiente
        SchedulerService.getByDay(nextDateISO, idlaboratory, token, { signal }),
      ])

      // Verificar si el componente sigue montado
      if (!isMounted.current) return Promise.resolve([])

      // Combinar los resultados de ambos días
      const hourListData = [
        ...(Array.isArray(currentDayData) ? currentDayData : []),
        ...(Array.isArray(nextDayData) ? nextDayData : []),
      ]

      // Guardar los datos de horarios para mostrar en la lista
      if (isMounted.current) {
        setScheduleData(hourListData)
        // Consultar disponibilidad de cada hora para cada pod (sin await, se ejecuta en paralelo)
        checkAvailabilityForAllHours()
      }

      // Crear una copia del objeto keyHours actual para no modificar el estado directamente
      const updatedKeyHours = { ...keyHours }

      // Procesar cada reserva para actualizar las horas disponibles
      // Las reservas del servidor vienen en UTC 0, convertirlas a hora local para comparar
      if (Array.isArray(hourListData)) {
        hourListData.forEach((reserve) => {
          const podId = reserve.pod_id
          const startDate = DateTime.fromISO(reserve.startDate, { zone: 'utc' }).toLocal()
          const endDate = DateTime.fromISO(reserve.endDate, { zone: 'utc' }).toLocal()

          if (updatedKeyHours[podId]) {
            const hoursToRemove = []
            for (let hour = startDate.hour; hour < endDate.hour; hour++) {
              hoursToRemove.push(hour)
            }

            updatedKeyHours[podId] = updatedKeyHours[podId].filter(
              (hourObj) => !hoursToRemove.includes(hourObj.value.hour)
            )
          }
        })
      }

      // Actualizar el estado solo si el componente sigue montado
      if (isMounted.current) {
        setKeyHours(updatedKeyHours)
        // Crear las opciones de hora de inicio después de actualizar keyHours
        setTimeout(() => {
          if (isMounted.current) {
            createHourStartSelect()
          }
        }, 0)
      }

      return hourListData
    } catch (e) {
      if (e.name === 'AbortError') {
        return Promise.resolve([])
      }
      if (isMounted.current) {
        setError(e.message)
      }
      return Promise.reject(e)
    }
  }

  // Función para encontrar un pod disponible para el rango de horas seleccionado
  const findAvailablePod = (startISO, endISO) => {
    if (!startISO || !endISO || !keyHours) return null

    // Las horas vienen en hora local, parsearlas directamente
    const startDateTime = DateTime.fromISO(startISO)
    const endDateTime = DateTime.fromISO(endISO)

    if (!startDateTime.isValid || !endDateTime.isValid) {
      return null
    }

    const startHourNum = startDateTime.hour
    const endHourNum = endDateTime.hour

    // Crear un array con todas las horas en el rango
    const hoursInRange = []
    for (let hour = startHourNum; hour < endHourNum; hour++) {
      hoursInRange.push(hour)
    }

    // Verificar cada pod
    for (const [podId, availableHours] of Object.entries(keyHours)) {
      if (!Array.isArray(availableHours) || availableHours.length === 0) {
        continue
      }

      // Verificar si todas las horas del rango están disponibles en este pod
      const availableHoursValues = availableHours
        .map((hourObj) => {
          if (hourObj.value && hourObj.value.hour !== undefined) {
            return hourObj.value.hour
          }
          return null
        })
        .filter((hour) => hour !== null)

      // Verificar si todas las horas del rango están en las horas disponibles
      const allHoursAvailable = hoursInRange.every((hour) => availableHoursValues.includes(hour))

      if (allHoursAvailable) {
        return podId
      }
    }

    return null
  }

  // Manejar envío del formulario
  const hadleSubmit = (e) => {
    e.preventDefault()
    if (!startHour || !endHour) {
      showGrowl('warn', 'Atención!', 'Debe seleccionar una hora de inicio y una hora de fin')
      return
    }

    // Buscar un pod disponible para el rango seleccionado
    const availablePodId = findAvailablePod(startHour, endHour)

    if (!availablePodId) {
      showGrowl('error', 'Error!', 'No hay pods disponibles para el rango de horas seleccionado')
      return
    }

    // Convertir las fechas de hora local a UTC 0 para enviar al servidor
    const startDateTime = DateTime.fromISO(startHour)
      .toUTC()
      .set({ millisecond: 0, minute: 0, second: 0 })
    const endDateTime = DateTime.fromISO(endHour)
      .toUTC()
      .set({ millisecond: 0, minute: 0, second: 0 })

    const data = {
      startDate: startDateTime.toISO({ suppressMilliseconds: true, includeOffset: false }),
      endDate: endDateTime.toISO({ suppressMilliseconds: true, includeOffset: false }),
      user_id: userLogged.id,
      laboratory_id: Number(idlaboratory),
      pod_id: availablePodId,
    }
    onSubmit(data)
  }

  // Función para simplificar la obtención del valor seleccionado en los components Select
  const getSelectedOption = (value, options, isHourSelect = true) => {
    if (!value || !options || !Array.isArray(options) || options.length === 0) {
      return null
    }

    // Para selects de hora, buscar por hora en hora local
    if (isHourSelect) {
      let hour

      // Extraer la hora dependiendo del tipo de valor
      if (typeof value === 'string') {
        // Si es un string ISO, parsear en hora local
        hour = DateTime.fromISO(value).hour
      } else if (typeof value === 'object' && value.hour !== undefined) {
        // Si es un objeto DateTime
        hour = value.hour
      } else {
        return null
      }

      // Buscar la opción que coincide con la hora
      return options.find((option) => {
        const label = option.label
        const labelHour = parseInt(label)
        return labelHour === hour
      })
    }

    // Para otros tipos de select, buscar por valor directo
    return options.find((option) => option.value === value)
  }

  // Consultar disponibilidad de cada hora para cada pod
  const checkAvailabilityForAllHours = async () => {
    if (!isMounted.current) return
    if (
      !podAvaiblesForReserve ||
      !podAvaiblesForReserve.freePods ||
      podAvaiblesForReserve.freePods.length === 0
    ) {
      console.log('⚠️ No hay pods para verificar disponibilidad')
      return
    }

    try {
      const today = DateTime.local()
      const isToday = selectedDay.hasSame(today, 'day')
      const startHour = isToday
        ? process.env.REACT_APP_IS_DEVELOPMENT === 'true'
          ? today.hour
          : today.hour + 1
        : 0

      const availabilityMap = {}
      const pods = podAvaiblesForReserve.freePods

      // Crear todas las consultas para cada hora y cada pod
      const availabilityChecks = []

      for (let hour = startHour; hour < 24; hour++) {
        const hourKey = `${hour}:00`
        const startDateTime = selectedDay.set({ hour, minute: 0, second: 0, millisecond: 0 })
        const endDateTime = startDateTime.plus({ hours: 1 })

        // Convertir a UTC para la consulta
        const startDateUTC = startDateTime
          .toUTC()
          .toISO({ suppressMilliseconds: true, includeOffset: false })
        const endDateUTC = endDateTime
          .toUTC()
          .toISO({ suppressMilliseconds: true, includeOffset: false })

        // Consultar disponibilidad para cada pod en esta hora
        pods.forEach((pod) => {
          const podId = pod.id || pod._id
          availabilityChecks.push(
            SchedulerService.getAvaiblePod(startDateUTC, endDateUTC, podId, token)
              .then((response) => ({
                hour: hourKey,
                hourNum: hour,
                podId,
                available: response.available || false,
              }))
              .catch((error) => {
                console.error(
                  `Error verificando disponibilidad para ${hourKey} pod ${podId}:`,
                  error
                )
                return {
                  hour: hourKey,
                  hourNum: hour,
                  podId,
                  available: false, // En caso de error, asumir no disponible
                }
              })
          )
        })
      }

      // Ejecutar todas las consultas en paralelo
      const results = await Promise.all(availabilityChecks)

      // Procesar resultados y contar disponibles por hora
      results.forEach((result) => {
        if (!availabilityMap[result.hour]) {
          availabilityMap[result.hour] = {
            hour: result.hour,
            hourNum: result.hourNum,
            available: 0,
            total: pods.length,
          }
        }

        if (result.available) {
          availabilityMap[result.hour].available++
        }
      })

      if (isMounted.current) {
        setAvailabilityByHour(availabilityMap)
      }
    } catch (error) {
      console.error('Error al verificar disponibilidad:', error)
    }
  }

  // Procesar datos para mostrar en la lista
  const getGroupedHours = () => {
    const grouped = {}

    // Usar los datos de disponibilidad calculados
    Object.values(availabilityByHour).forEach((hourData) => {
      grouped[hourData.hour] = {
        hour: hourData.hour,
        hourNum: hourData.hourNum,
        available: hourData.available,
        total: hourData.total,
      }
    })

    return Object.values(grouped).sort((a, b) => a.hourNum - b.hourNum)
  }

  const groupedHours = getGroupedHours()

  return (
    <form disabled={isLoading} onSubmit={hadleSubmit}>
      <Growl ref={growlRef} />
      <div className="scheduler-form-container">
        {/* Lado izquierdo: Calendario y formulario */}
        <div className="form-section">
          <div className="form-group">
            <h2>Día de reserva</h2>

            {error && (
              <Message severity="error" text={error} style={{ marginBottom: '20px' }}></Message>
            )}
            <Calendar
              className="calendar-container"
              minDate={new Date()}
              onChange={convertDate}
              value={selectedDay ? selectedDay.toJSDate() : new Date()}
            />

            <div className="select-container">
              <Select
                placeholder="Hora inicio"
                name="startHour"
                options={startHourRows}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={handleStarSelectChange}
                value={getSelectedOption(startHour, startHourRows)}
              />
              <Select
                placeholder="Hora fin"
                name="endHour"
                options={endHourRows}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={handleEndSelectChange}
                value={getSelectedOption(endHour, endHourRows)}
                isDisabled={!startHour}
                noOptionsMessage={() =>
                  `${
                    endHourRows.length === 0
                      ? 'No hay opciones disponibles'
                      : 'Seleccione una opción'
                  }`
                }
              />
            </div>

            <div className="button-group">
              <div className="button">
                <Button
                  label={isLoading || isLoadingData.current ? 'Guardando...' : 'Guardar'}
                  disabled={isLoading || isLoadingData.current || !startHour || !endHour}
                  type="submit"
                  className="p-button-primary"
                />
              </div>

              <div className="button">
                <Link to={cancelLink}>
                  <Button
                    label="Cancelar"
                    disabled={isLoading}
                    className="p-button-secondary"
                    type="button"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Lado derecho: Lista de horarios */}
        <div className="schedule-list-section">
          <h3>
            Horarios{' '}
            {selectedDay
              ? `- ${selectedDay.toLocaleString({
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}`
              : ''}
          </h3>

          {isLoadingData.current ? (
            <div className="loading-container">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }}></i>
              <p>Cargando horarios...</p>
            </div>
          ) : groupedHours.length === 0 ? (
            <div className="empty-state">
              <i className="pi pi-calendar-times" style={{ fontSize: '3em', color: '#ccc' }}></i>
              <p>No hay horarios disponibles para esta fecha</p>
            </div>
          ) : (
            <div className="schedule-list">
              {groupedHours.map((group) => {
                const isAvailable = group.available > 0
                return (
                  <div
                    key={group.hour}
                    className={`hour-group ${isAvailable ? 'available' : 'unavailable'}`}
                  >
                    <div className="hour-header">
                      <span className="hour-label">{group.hour}</span>
                      {isAvailable ? (
                        <span className="status-badge available">
                          {group.available} Disponible{group.available > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="status-badge unavailable">No disponible</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </form>
  )
}

SchedulForm.propTypes = {
  idlaboratory: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  cancelLink: PropTypes.string.isRequired,
  selectedDay: PropTypes.object.isRequired,
  setSelectedDay: PropTypes.func.isRequired,
  keyHours: PropTypes.object.isRequired,
  setKeyHours: PropTypes.func.isRequired,
  podAvaiblesForReserve: PropTypes.object,
}
